import { flow, getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  APIService,
  DownloadParams,
  LoginParams,
  RegisterParams,
  UploadParams,
  UploadClientParams,
  UploadToS3StorageParams,
  CheckVersionParams,
  UploadClientResult,
  ForgetPasswordParams,
  ResetPasswordParams,
  RevisePasswordParams,
  VerifyParams,
  PhoneSmsParams,
  CreatePayPwdParams,
  UpdatePayPwdParams,
  CreatePayParams,
  GetParams,
  DeletePayParams,
  CreateExchangeParams,
  CancelExchangeParams,
  SaveComplaintParams,
  MyCreditParams,
  SaveCreditParams,
  EmailSmsParams,
  UpdateEmailParams,
  FindPasswordParams,
  UpdatePasswordParams
} from "~services/api"
import { RootStoreModel } from "~models/root-store"
import { getJPushRegistrationId } from "~services/jpush"
/**
 * 管理API请求
 */
export const APIStoreModel = types
  .model("ApiStore")
  .props({})
  .actions((self) => {
    const { configStore, authStore } = getRoot<Instance<typeof RootStoreModel>>(self)

    // http client
    const api = new APIService(configStore.api)

    /** 登录/注册前取 JPush registrationId 并附带设备信息 */
    const loginDeviceExtras = flow(function* () {
      const rootStore = getRoot<Instance<typeof RootStoreModel>>(self)
      let registrationId = authStore.clientId?.trim() || ""
      try {
        const freshId = yield getJPushRegistrationId(rootStore)
        if (freshId?.trim()) {
          registrationId = freshId.trim()
        }
      } catch {
        // web / 原生模块不可用等场景忽略
      }
      return {
        device_id: authStore.deviceId,
        device_name: authStore.deviceName,
        ...(registrationId ? { registration_id: registrationId } : {}),
      }
    })

    // 注册账号
    const register = flow(function* (data: RegisterParams) {
      const extras = yield loginDeviceExtras()
      return yield api.register({ ...data, ...extras })
    })

    // 登录账号
    const login = flow(function* (data: LoginParams) {
      const extras = yield loginDeviceExtras()
      return yield api.login({ ...data, ...extras })
    })

    // 忘记密码
    const forgetPassword = flow(function* (data: ForgetPasswordParams) {
      const params = { ...data }
      return yield api.forgetPassword(params)
    })

    // 重置密码
    const resetPassword = flow(function* (data: ResetPasswordParams) {
      const params = { ...data }
      return yield api.resetPassword(params)
    })

    // 修改密码
    const revisePassword = flow(function* (data: RevisePasswordParams) {
      const params = { ...data }
      return yield api.revisePassword(params)
    })

    // 上传, 状态独立管理，因为可能会存在多任务同时执行的情况
    const upload = flow(function* (data: UploadParams) {
      return yield api.upload(data)
    })

    const uploadClient = flow(function* (data: UploadClientParams) {
      return yield api.uploadClient(data)
    })

    const uploadToS3Storage = flow(function* (
      s3url: UploadClientResult["url"],
      formData: UploadToS3StorageParams,
      onProgressUpdate: (progress: number) => void,
    ) {
      return yield api.uploadToS3Storage(s3url, formData, onProgressUpdate)
    })

    // 下载
    const download = flow(function* (data: DownloadParams) {
      return yield api.download(data)
    })

    const checkVersion = flow(function* (data: CheckVersionParams) {
      return yield api.checkVersion(data)
    })

    const getWalletInfo = flow(function* (data: GetParams) {
      return yield api.getWalletInfo(data)
    })
    const getWalletLog = flow(function* (data: GetParams) {
      return yield api.getWalletLog(data)
    })
    const getTakeOrders = flow(function* (data: GetParams) {
      return yield api.getTakeOrders(data)
    })
    const getTakeOrdersSold = flow(function* (data: GetParams) {
      return yield api.getTakeOrdersSold(data)
    })
    const getTakeOrdersDisputed = flow(function* (data: GetParams) {
      return yield api.getTakeOrdersDisputed(data)
    })
    const getForceCs = flow(function* (data: GetParams) {
      return yield api.getForceCs(data)
    })
    const getBankList = flow(function* () {
      return yield api.getBankList()
    })

    const getPaymentList = flow(function* (data: GetParams) {
      return yield api.getPaymentList(data)
    })

    const getPendingOrders = flow(function* (data: GetParams) {
      return yield api.getPendingOrders(data)
    })

    const getMyOrders = flow(function* (data: GetParams) {
      return yield api.getMyOrders(data)
    })

    const getMyOrderDetail = flow(function* (data: GetParams) {
      return yield api.getMyOrderDetail(data)
    })

    const deletePayment = flow(function* (data: DeletePayParams) {
      return yield api.deletePayment(data)
    })

    // 实名认证
    const verify = flow(function* (data: VerifyParams) {
      return yield api.verify(data)
    })

    // 实名认证
    const phoneVerify = flow(function* (data: PhoneSmsParams) {
      return yield api.phoneVerify(data)
    })

    // 手机短信验证
    const getPhoneSms = flow(function* (data: PhoneSmsParams) {
      return yield api.phoneSms(data)
    })

    // 手机短信验证
    const getPhoneSmsv1 = flow(function* (data: PhoneSmsParams) {
      return yield api.phoneSmsv1(data)
    })

    // 創建支付密码
    const paypws = flow(function* (data: CreatePayParams) {
      return yield api.paypws(data)
    })

    // 創建支付密码
    const payCheck = flow(function* (data: CreatePayPwdParams) {
      return yield api.payCheck(data)
    })

    // 修改支付密码
    const updatePaypws = flow(function* (data: UpdatePayPwdParams) {
      return yield api.updatePaypws(data)
    })

    // 創建支付
    const createPay = flow(function* (data: CreatePayParams, token: string) {
      return yield api.createPay(data, token)
    })

    // 抓匯率
    const getExchangeRate = flow(function* (data: GetParams) {
      return yield api.getExchangeRate(data)
    })

    // 抓匯率
    const getExchangeList = flow(function* (data: GetParams) {
      return yield api.getExchangeList(data)
    })

    // 創建兌換
    const createExchange = flow(function* (data: CreateExchangeParams) {
      return yield api.createExchange(data)
    })

    // 取消兌換
    const cancelExchange = flow(function* (data: CancelExchangeParams) {
      return yield api.cancelExchange(data)
    })

    // 申诉列表
    const getComplaintList = flow(function* (data: GetParams) {
      return yield api.getComplaintList(data)
    })

    // 提交申诉
    const saveComplaint = flow(function* (data: SaveComplaintParams) {
      return yield api.saveComplaint(data)
    })
    /**   2024-7-16 */

    // 评分规则
    const getRatingRule = flow(function* () {
      const token = authStore.getToken
      return yield api.getRatingRule({ token })
    })

    // 信用分规则
    const getCreditRule = flow(function* () {
      const token = authStore.getToken
      return yield api.getCreditRule({ token })
    })

    // 我的评价
    const getMyCredit = flow(function* (data: MyCreditParams) {
      const token = authStore.getToken
      return yield api.getMyCredit(data, token)
    })

    // 提交订单评价
    const saveCredit = flow(function* (data: SaveCreditParams) {
      const token = authStore.getToken
      return yield api.saveCredit(data, token)
    })

    // 邮箱验证码
    const getEmailSms = flow(function* (data: EmailSmsParams) {
      // const token = authStore.getToken
      return yield api.emailSms(data)
    })

    // 提交修改邮箱
    const updateEmail = flow(function* (data: UpdateEmailParams) {
      const token = authStore.getToken
      return yield api.updateEmail(data, token)
    })

    // 获取我加入的频道/群
    const getJoinChatGroupAndChannelList = flow(function* (params?: Record<string, unknown>) {
      const token = authStore.getToken
      return yield api.getJoinChatGroupAndChannelList({ token, params: params ?? {} })
    })

    // 获取群组/频道设置信息
    const getChannelGroupSettingInfo = flow(function* (params) {
      const token = authStore.getToken
      return yield api.getChannelGroupSettingInfo({ token, params })
    })

    // 更新群组/频道设置信息
    const updateChannelGroupSetting = flow(function* (params) {
      const token = authStore.getToken
      return yield api.updateChannelGroupSetting({ token, params })
    })

    // 发送手机验证码(未绑定手机)
    const sendPhoneCodeByNotBound = flow(function* (data: PhoneSmsParams) {
      return yield api.sendPhoneCodeByNotBound(data)
    })
    // 找回密码
    const findPassword = flow(function* (data: FindPasswordParams) {
      return yield api.findPassword(data)
    })

    // 更新密码
    const updatePassword = flow(function* (data: UpdatePasswordParams) {
      return yield api.updatePassword(data)
    })

    // 获取群聊数据统计
    const getGroupChatDataStatistics = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getGroupChatDataStatistics({ token, params })
    })

    // 获取群聊数据列表
    const getGroupChatDataList = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getGroupChatDataList({ token, params })
    })

    // 获取账户信息
    const getBindAccountInfo = flow(function* () {
      const token = authStore.getToken
      return yield api.getBindAccountInfo({ token })
    })

    // 获取当前用户的通知列表
    const getUserNotificationList = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getUserNotificationList({ token, params })
    })

    // 获取当前用户未读通知数量
    const getUserUnreadNotificationCount = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getUserUnreadNotificationCount({ token, params })
    })

    // 标记通知为已读
    const markNotificationAsRead = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.markNotificationAsRead({ token, params })
    })

    // 标记当前用户所有通知为已读
    const markAllNotificationsAsRead = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.markAllNotificationsAsRead({ token, params })
    })

    // 标记全部已读并重新请求通知中心列表
    const markAllNotificationsAsReadAndRefreshList = flow(function* (
      params: Record<string, string | number> = {},
      listParams: Record<string, string | number> = { page: 1, limit: 20 },
    ) {
      const token = authStore.getToken
      return yield api.markAllNotificationsAsReadAndRefreshList({ token, params }, listParams)
    })

    // 重新请求通知中心列表
    const refreshUserNotificationList = flow(function* (
      listParams: Record<string, string | number> = { page: 1, limit: 20 },
    ) {
      const token = authStore.getToken
      return yield api.getUserNotificationList({ token, params: listParams })
    })

    // 获取聊天室ID
    const getChatRoomId = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getChatRoomId({ token, params })
    })

    // 获取红包详情
    const getRedpacketDetail = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getRedpacketDetail({ token, params })
    })

    // 获取转账详情
    const getTransferDetail = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getTransferDetail({ token, params })
    })

    // 个人设置中心：保存设置
    const setPersonalCenterSetting = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.setPersonalCenterSetting({ token, params })
    })

    // 根据 chat_id + user_id 查询群成员的自动发包配置
    const getAutoSendRedpacketConfig = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getAutoSendRedpacketConfig({ token, params })
    })
    // 录入/更新群成员的自动发包配置
    const saveAutoSendRedpacketConfig = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.saveAutoSendRedpacketConfig({ token, params })
    })

    // 查询当前用户的谷歌验证码绑定信息
    const getGoogleAuthInfo = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getGoogleAuthInfo({ token, params })
    })
    // 绑定谷歌验证码
    const bindGoogleAuthCode = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.bindGoogleAuthCode({ token, params })
    })

    // 用户自助开启或关闭 2FA 谷歌验证
    const toggleGoogleAuth = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.toggleGoogleAuth({ token, params })
    })

    // 获取当前用户所有未注销的登录设备
    const getLoginDevices = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getLoginDevices({ token, params })
    })
    // 注销指定 device_id 的登录设备
    const logoutLoginDevice = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.logoutLoginDevice({ token, params })
    })
    // 注销除当前请求设备外的全部登录设备
    const logoutAllLoginDevices = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.logoutAllLoginDevices({ token, params })
    })
    // 双重验证
    const verifyGoogleAuthLogin = flow(function* (params: GetParams) {
      const extras = yield loginDeviceExtras()
      return yield api.verifyGoogleAuthLogin({ ...(params as any), ...extras })
    })
    // 新设备登录验证
    const verifyNewDeviceLogin = flow(function* (params: GetParams) {
      const extras = yield loginDeviceExtras()
      return yield api.verifyNewDeviceLogin({ ...(params as any), ...extras })
    })
    // 查询消息自毁设置
    const getMessageDestroySetting = flow(function* (params: GetParams) {
      const token = authStore.getToken
      return yield api.getMessageDestroySetting({ token, params })
    })
    return {
      register,
      login,
      forgetPassword,
      resetPassword,
      revisePassword,
      upload,
      uploadClient,
      uploadToS3Storage,
      download,
      checkVersion,
      getWalletInfo,
      getWalletLog,
      getTakeOrders,
      getTakeOrdersSold,
      getTakeOrdersDisputed,
      getForceCs,
      verify,
      getPhoneSms,
      getPhoneSmsv1,
      phoneVerify,
      paypws,
      updatePaypws,
      createPay,
      getPaymentList,
      deletePayment,
      getPendingOrders,
      getMyOrders,
      getMyOrderDetail,
      payCheck,
      getBankList,
      getExchangeRate,
      createExchange,
      getExchangeList,
      cancelExchange,
      getComplaintList,
      saveComplaint,
      getRatingRule,
      getCreditRule,
      getMyCredit,
      saveCredit,
      getEmailSms,
      updateEmail,
      getJoinChatGroupAndChannelList,
      getChannelGroupSettingInfo,
      updateChannelGroupSetting,
      sendPhoneCodeByNotBound,
      findPassword,
      getGroupChatDataStatistics,
      updatePassword,
      getGroupChatDataList,
      getBindAccountInfo,
      getUserNotificationList,
      getUserUnreadNotificationCount,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      markAllNotificationsAsReadAndRefreshList,
      refreshUserNotificationList,
      getChatRoomId,
      getRedpacketDetail,
      getTransferDetail,
      setPersonalCenterSetting,
      getAutoSendRedpacketConfig,
      saveAutoSendRedpacketConfig,
      getGoogleAuthInfo,
      bindGoogleAuthCode,
      toggleGoogleAuth,
      getLoginDevices,
      logoutLoginDevice,
      logoutAllLoginDevices,
      verifyGoogleAuthLogin,
      verifyNewDeviceLogin,
      getMessageDestroySetting
    }
  })

type ApiStoreType = Instance<typeof APIStoreModel>
export interface ApiStore extends ApiStoreType {}
type ApiStoreSnapshotType = SnapshotOut<typeof APIStoreModel>
export interface ApiStoreSnapshot extends ApiStoreSnapshotType {}
